import { useQuery } from '@tanstack/react-query';
import { HeartIcon, MapPinIcon, Share2Icon, TriangleAlertIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import axios from 'axios';

import { WhatsAppIcon } from '@/components/Icons';
import { ReportPub } from '@/components/ReportPub/ReportPub';
import { SimpleMap } from '@/components/Map/SimpleMap';
import { Comments } from '@/components/Comments/Comments';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { NbButton, NbCard } from '@/components/Neobrut';
import {
  ANIMAL_AGES_CHOICES,
  ANIMAL_GENDERS,
  ANIMAL_TYPES,
  API_BASE_URL,
  PET_COLORS,
} from '@/constants';
import { useAuthToken } from '@/hooks/useAuthToken';
import { PublicationSkeleton } from '@/components/Skeletons/PublicationSkeleton';

const WHATSAPP_MESSAGE = '¡Hola! Te contacto porque publicaste esta mascota en RescatApp: ';

export const Publication: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, isTokenExpired, userIdFromToken } = useAuthToken();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const {
    data: pet,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [`pet-${id}`],
    queryFn: async (): Promise<Pet> => {
      const response = await axios.get(`${API_BASE_URL}/pets/${id}`);
      return response.data;
    },
  });

  const date = useMemo(() => {
    if (!pet) return;
    if (pet.status === 'found' && pet.date_found) {
      return new Date(pet.date_found).toLocaleDateString('es-ES');
    }
    if (pet.status === 'lost' && pet.date_lost) {
      return new Date(pet.date_lost).toLocaleDateString('es-ES');
    }
    return new Date().toLocaleDateString('es-ES');
  }, [pet]);

  const daysAgo = useMemo(() => {
    if (!pet) return null;
    const target = pet.status === 'lost' ? pet.date_lost : pet.date_found;
    if (!target) return null;
    const diff = (Date.now() - new Date(target).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.floor(diff));
  }, [pet]);

  const addressFull = useMemo(() => {
    if (!pet?.location?.address) return 'Dirección no disponible';
    const { road, house_number, city, state } = pet.location.address;
    return [road && `${road}${house_number ? ` ${house_number}` : ''}`, city, state]
      .filter(Boolean)
      .join(', ');
  }, [pet]);

  const handleAddFavorite = useCallback(async () => {
    if (token && !isTokenExpired(token) && pet) {
      const response = await axios.post(
        `${API_BASE_URL}/favorites/`,
        { pet: pet.id, user: userIdFromToken(token) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.status === 200 || response.status === 201) {
        toast.success('Agregada a favoritos');
      }
      return;
    }
    if (!token || isTokenExpired(token)) {
      toast.info('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
      navigate('/login');
    }
  }, [token, pet, navigate, isTokenExpired, userIdFromToken]);

  const images = useMemo(() => {
    if (!pet) return [];
    return pet.gallery.map((url) => (
      <img
        className={`h-full w-full object-cover ${loading ? 'hidden' : 'block'}`}
        key={url}
        src={url}
        alt="pet"
        onLoad={() => setLoading(false)}
        onError={() => setLoading(false)}
      />
    ));
  }, [pet, loading]);

  const handleCopyShareLink = useCallback(() => {
    const link = `https://rescatapp.com.ar/pet/${id}/`;
    navigator
      .share({ url: link })
      .then(() => console.log('Link compartido'))
      .catch((err) => console.error('Error al copiar el link: ', err));
  }, [id]);

  const handleOpenWhatsAppTab = () => {
    if (pet && pet.uploaded_by.phone_number) {
      const urlEncodedMessage = encodeURIComponent(
        `${WHATSAPP_MESSAGE} https://rescatapp.com.ar/pet/${pet.id}`,
      );
      const whatsappUrl = `https://wa.me/${pet.uploaded_by.phone_number.replace('+', '')}?text=${urlEncodedMessage}`;
      window.open(whatsappUrl, '_blank');
    } else {
      toast.error('No se puede abrir WhatsApp. Número de teléfono no disponible.');
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-md px-3">
        <PublicationSkeleton />
      </div>
    );
  }

  if (isError || !pet) {
    return <div className="px-4 py-16 text-center">Ha ocurrido un error. Recargá la página.</div>;
  }

  const isLost = pet.status === 'lost';
  const statusFill = isLost ? 'fill-lost' : 'fill-found';
  const statusLabel = isLost ? 'SE BUSCA · PERDIDO' : '¿LO CONOCÉS? · ENCONTRADO';

  const spec: [string, string | null][] = [
    ['Especie', ANIMAL_TYPES[pet.type] ?? pet.type],
    ['Raza', pet.race || '—'],
    ['Sexo', `${ANIMAL_GENDERS[pet.gender] ?? pet.gender} ${pet.gender === 'm' ? '♂' : pet.gender === 'f' ? '♀' : ''}`],
    ['Edad', ANIMAL_AGES_CHOICES[pet.age] ?? pet.age],
    ['Color', PET_COLORS[pet.main_color] ?? pet.main_color ?? '—'],
    ['Esterilizado', pet.sterilized ? 'Sí' : 'No'],
    ['Retenido', pet.retained ? 'Sí' : 'No'],
  ];

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      {/* Top actions */}
      <div className="flex items-center justify-between px-4 pt-1">
        <NbButton fill="paper" size="sm" onClick={() => navigate(-1)}>
          ← volver
        </NbButton>
        <div className="flex gap-1.5">
          <NbButton fill="paper" size="sm" onClick={handleAddFavorite} aria-label="Favorito">
            <HeartIcon size={12} />
          </NbButton>
          <NbButton fill="paper" size="sm" onClick={handleCopyShareLink} aria-label="Compartir">
            <Share2Icon size={12} />
          </NbButton>
          <NbButton
            fill="paper"
            size="sm"
            onClick={() => setIsReportDialogOpen((v) => !v)}
            aria-label="Reportar"
          >
            <TriangleAlertIcon size={12} color="red" />
          </NbButton>
        </div>
      </div>

      {/* Status band */}
      <div className="px-4 pt-4">
        <div
          className={`${statusFill} flex items-center gap-2.5 px-3.5 py-2.5`}
          style={{
            border: '3px solid var(--nb-ink)',
            boxShadow: '6px 6px 0 var(--nb-ink)',
            borderRadius: 'var(--nb-radius)',
          }}
        >
          <TriangleAlertIcon size={22} />
          <div>
            <div className="font-display text-[16px] leading-none">{statusLabel}</div>
            <div
              className="mt-0.5 text-[10px]"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              CASO #{String(pet.id).padStart(4, '0')}
              {daysAgo !== null && ` · hace ${daysAgo}d`}
            </div>
          </div>
        </div>
      </div>

      {/* Photo carousel */}
      <div className="px-4 pt-4">
        <NbCard className="relative p-0">
          <Carousel className="mx-auto w-full">
            <CarouselContent>
              {loading && (
                <CarouselItem>
                  <div className="nb-hatch flex aspect-square items-center justify-center bg-[#e8e3d5]">
                    <div
                      className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
                      style={{ fontFamily: '"JetBrains Mono", monospace' }}
                    >
                      Cargando…
                    </div>
                  </div>
                </CarouselItem>
              )}
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="flex aspect-square items-center justify-center bg-[#e8e3d5]">
                    {image}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="absolute left-2 top-1/2 z-10 -translate-y-1/2" />
            <CarouselNext className="absolute right-2 top-1/2 z-10 -translate-y-1/2" />
          </Carousel>
        </NbCard>
      </div>

      {/* Name block */}
      <div className="px-4 pt-4">
        <div className="flex items-baseline justify-between gap-2">
          <h1 className="font-display text-[44px] leading-[0.9]">
            {pet.name?.trim() ? pet.name : isLost ? 'Sin nombre' : '¿Lo conocés?'}
          </h1>
          {pet.solved_case && (
            <div
              className="fill-solved border-2 px-2 py-1 text-[11px]"
              style={{ borderColor: 'var(--nb-ink)', transform: 'rotate(3deg)', fontFamily: '"Archivo Black", sans-serif' }}
            >
              ✓ RESUELTO
            </div>
          )}
        </div>
        {pet.observations && (
          <div className="mt-1 text-[14px] font-semibold leading-snug">{pet.observations}</div>
        )}
      </div>

      {/* Data sheet */}
      <div className="px-4 pt-4">
        <NbCard className="p-0">
          <div
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              background: 'var(--nb-ink)',
              color: 'var(--nb-paper)',
            }}
          >
            FICHA · {pet.name || '—'}
          </div>
          <div className="px-3">
            {spec.map(([k, v], i, arr) => (
              <div
                key={k}
                className="flex justify-between py-2 text-[13px]"
                style={{
                  borderBottom:
                    i === arr.length - 1 ? 'none' : '1.5px solid rgba(10,10,10,0.15)',
                }}
              >
                <span className="text-[11px] font-bold uppercase opacity-70">{k}</span>
                <span className="font-semibold">{v ?? '—'}</span>
              </div>
            ))}
          </div>
        </NbCard>
      </div>

      {/* Last seen */}
      <div className="px-4 pt-4">
        <div
          className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          / {isLost ? 'última vista' : 'encontrado en'}
        </div>
        <NbCard className="p-0">
          <div className="relative h-48 w-full overflow-hidden" style={{ borderBottom: '3px solid var(--nb-ink)' }}>
            <SimpleMap
              lat={pet.location.lat}
              lon={pet.location.lon}
              className="absolute inset-0 h-full w-full"
            />
          </div>
          <div className="flex items-start gap-2 px-3 py-2 text-[13px] font-semibold">
            <MapPinIcon size={14} className="mt-0.5 shrink-0" />
            <div>
              <div>{addressFull}</div>
              <div
                className="mt-0.5 text-[10px] opacity-70"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {date} · radio aprox.
              </div>
            </div>
          </div>
        </NbCard>
      </div>

      {!pet.retained && pet.status === 'found' && (
        <div className="px-4 pt-3">
          <div
            className="text-[12px] font-semibold"
            style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
          >
            ⚠ Esta mascota no está retenida. Puede no encontrarse en este lugar.
          </div>
        </div>
      )}

      {/* Contact CTAs */}
      <div className="grid grid-cols-2 gap-2.5 px-4 pt-4">
        <NbButton fill="solved" onClick={handleOpenWhatsAppTab} className="py-3.5 text-[13px]">
          <WhatsAppIcon /> Contactar
        </NbButton>
        <NbButton fill="paper" onClick={handleCopyShareLink} className="py-3.5 text-[13px]">
          <Share2Icon size={14} /> Compartir
        </NbButton>
      </div>

      {/* Report sighting */}
      <div className="px-4 pt-4">
        <NbCard className="fill-ink p-4 text-white">
          <div className="font-display text-[18px] leading-none" style={{ color: 'var(--nb-paper)' }}>
            ¿{isLost ? 'Lo viste?' : 'Lo reconocés?'}
          </div>
          <div className="mt-1 text-[12px] opacity-85">
            {isLost
              ? 'Reportá una vista aunque no puedas agarrarlo. Cada dato cuenta.'
              : 'Dejá un comentario. Cualquier señal ayuda a dar con la familia.'}
          </div>
        </NbCard>
      </div>

      <Comments petId={pet.id} petStatus={pet.status} />

      <ReportPub
        open={isReportDialogOpen}
        onClose={() => setIsReportDialogOpen(false)}
        post={pet}
      />
    </div>
  );
};
